import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expectStatus } from '@/lib/api/client';
import { disconnectMyAiTools, listMyAiTools } from '@/lib/api/generated/api';
import type { AiToolList } from '@/lib/api/generated/model';

import { EDITOR_COPY } from './copy';
import { editorAccessKeys } from './queryKeys';

/** The read behind `useMyAiTools`, for a caller that needs its own options. */
export const myAiToolsQuery = {
  queryKey: editorAccessKeys.myTools(),
  queryFn: async ({ signal }: { signal: AbortSignal }): Promise<AiToolList> => {
    const response = await listMyAiTools({ signal });
    return expectStatus(response, 200, 'Could not read your AI tools.');
  },
};

/** The AI tools that have worked as the signed-in person, latest first (spec 245). */
export function useMyAiTools() {
  return useQuery<AiToolList>(myAiToolsQuery);
}

/**
 * End every AI tool's access at once (spec 247, service 204). Refused, it
 * throws the service's reason: the tools' current access may have ended while
 * they could still sign back in, and the person should know which.
 */
export function useDisconnectAiTools() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await disconnectMyAiTools();
      if (response.status === 204) return;
      const body = response.data as { message?: unknown } | undefined;
      throw new Error(
        typeof body?.message === 'string'
          ? body.message
          : EDITOR_COPY.disconnectFailed,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: editorAccessKeys.myTools(),
      });
    },
  });
}
